/**
 * Manifesto de mídia da página /exemplos (botão VER EXEMPLOS do first-touch WhatsApp).
 *
 * Nada aqui é gerado em runtime: a página só renderiza o que estiver preenchido.
 * Enquanto `src` for string vazia, o slot correspondente aparece como
 * "Mídia entra aqui". A página nunca substitui a entrega real por mídia de stock
 * nem pelos MP4 curtos do Hero da home (`videos-site/*.mp4`), que são amostras de
 * 15s e não representam o produto.
 *
 * Os três planos reusam os mesmos 6 casos. O que muda é o formato da entrega.
 */

import { PORTFOLIO_LYRICS } from './portfolio-lyrics';

/**
 * Os três planos que a página explica, na mesma ordem em que aparecem na tela.
 * Estes campos alimentam o resumo do topo E o cabeçalho de cada seção, então
 * preço e nome mudam nos dois lugares de uma vez. O público é 50+ com pouca
 * familiaridade digital: cada frase fala de uma coisa só e evita jargão.
 */
export type Plan = {
  id: string;
  /** Âncora do `<section>` correspondente. */
  anchor: string;
  price: string;
  name: string;
  /** Uma frase: o que chega na mão da pessoa. */
  receives: string;
  /** Uma frase: o que este plano não inclui. */
  missing?: string;
  /** Frase curta de destaque, usada quando não há o que listar como ausente. */
  note?: string;
};

export const PLANS: Plan[] = [
  {
    id: 'audio',
    anchor: 'audio',
    price: 'R$ 29,90',
    name: 'Só a música, no WhatsApp',
    receives: 'A música completa em áudio, que chega aqui mesmo na conversa.',
    missing: 'Não tem vídeo e não tem página com a letra.',
  },
  {
    id: 'pagina',
    anchor: 'pagina',
    price: 'R$ 39,90',
    name: 'A música com página e letra',
    receives: 'A música completa em uma página com a letra escrita na tela.',
    missing: 'Não tem vídeo com as fotos.',
  },
  {
    id: 'video',
    anchor: 'video',
    price: 'R$ 49,90',
    name: 'A música com vídeo e fotos',
    receives: 'A música completa dentro de um vídeo, com as fotos da pessoa.',
    note: 'É o mais completo: vem também com o áudio e a página com a letra.',
  },
];

export type MediaSlot = {
  /** URL pública definitiva (Storage/Blob). String vazia = slot ainda vazio. */
  src: string;
  /** Imagem leve de capa (JPG/WebP, ~720px de largura). */
  poster?: string;
  /** Duração real do arquivo, ex.: "3:47". Só preencha depois que a mídia existir. */
  duration?: string;
};

/**
 * Um gênero = uma história. Os três planos mostram esta mesma história
 * no formato da entrega: áudio no WhatsApp, página com letra, ou vídeo com fotos.
 */
export type GenreExample = {
  id: string;
  genre: string;
  occasion: string;
  recipient: string;
  /** Quantas fotos entraram neste vídeo. Só é exibido quando o vídeo existe. */
  photoCount?: number;
  video: MediaSlot;
  audio: MediaSlot;
  page: {
    slug: string;
    title: string;
    cover: string;
    lyrics: string;
  };
};

/** Entrega do essencial: só o MP3, como mensagem de voz no WhatsApp. */
export type WhatsAppAudioExample = {
  id: string;
  recipient: string;
  genre: string;
  occasion: string;
  audio: MediaSlot;
};

/** Entrega com página própria: player + letra, no mesmo formato de /ouvir/:slug. */
export type OuvirPageExample = {
  id: string;
  slug: string;
  name: string;
  title: string;
  genre: string;
  occasion: string;
  cover?: string;
  audio: MediaSlot;
  lyrics: string;
};

const STORAGE = 'https://frbdhxiwkwjawdifsiya.supabase.co/storage/v1/object/public/exemplos/portfolio-20260902';

export const hasMedia = (slot: MediaSlot): boolean => slot.src.trim().length > 0;

export const GENRE_EXAMPLES: GenreExample[] = [
  {
    id: 'gospel',
    genre: 'Gospel',
    occasion: 'Aniversário da mãe',
    recipient: 'Dulce',
    photoCount: 10,
    video: { src: `${STORAGE}/gospel/video.mp4`, poster: `${STORAGE}/gospel/poster.jpg`, duration: '4:39' },
    audio: { src: `${STORAGE}/gospel/audio.mp3`, duration: '4:39' },
    page: {
      slug: 'dulce-minha-oracao',
      title: 'Dulce, minha oração',
      cover: `${STORAGE}/gospel/cover.jpg`,
      lyrics: PORTFOLIO_LYRICS.gospel,
    },
  },
  {
    id: 'sertanejo',
    genre: 'Sertanejo',
    occasion: 'Bodas de casamento',
    recipient: 'Valmir',
    photoCount: 10,
    video: { src: `${STORAGE}/sertanejo/video.mp4`, poster: `${STORAGE}/sertanejo/poster.jpg`, duration: '4:48' },
    audio: { src: `${STORAGE}/sertanejo/audio.mp3`, duration: '4:48' },
    page: {
      slug: 'valmir-meu-bem',
      title: 'Valmir, meu bem',
      cover: `${STORAGE}/sertanejo/cover.jpg`,
      lyrics: PORTFOLIO_LYRICS.sertanejo,
    },
  },
  {
    id: 'mpb',
    genre: 'MPB',
    occasion: 'Homenagem ao pai',
    recipient: 'Hélio',
    photoCount: 10,
    video: { src: `${STORAGE}/mpb/video.mp4`, poster: `${STORAGE}/mpb/poster.jpg`, duration: '4:25' },
    audio: { src: `${STORAGE}/mpb/audio.mp3`, duration: '4:25' },
    page: {
      slug: 'helio-meu-chao',
      title: 'Hélio, meu chão',
      cover: `${STORAGE}/mpb/cover.jpg`,
      lyrics: PORTFOLIO_LYRICS.mpb,
    },
  },
  {
    id: 'pagode',
    genre: 'Pagode',
    occasion: 'Aniversário do amigo',
    recipient: 'Ruan',
    photoCount: 10,
    video: { src: `${STORAGE}/pagode/video.mp4`, poster: `${STORAGE}/pagode/poster.jpg`, duration: '3:48' },
    audio: { src: `${STORAGE}/pagode/audio.mp3`, duration: '3:48' },
    page: {
      slug: 'ruan-irmao-de-escolha',
      title: 'Ruan, irmão de escolha',
      cover: `${STORAGE}/pagode/cover.jpg`,
      lyrics: PORTFOLIO_LYRICS.pagode,
    },
  },
  {
    id: 'forro',
    genre: 'Forró',
    occasion: 'Aniversário da avó',
    recipient: 'Nazaré',
    photoCount: 10,
    video: { src: `${STORAGE}/forro/video.mp4`, poster: `${STORAGE}/forro/poster.jpg`, duration: '3:43' },
    audio: { src: `${STORAGE}/forro/audio.mp3`, duration: '3:43' },
    page: {
      slug: 'vo-nazare',
      title: 'Vó Nazaré',
      cover: `${STORAGE}/forro/cover.jpg`,
      lyrics: PORTFOLIO_LYRICS.forro,
    },
  },
  {
    id: 'pop',
    genre: 'Pop',
    occasion: '15 anos da filha',
    recipient: 'Giovana',
    photoCount: 10,
    video: { src: `${STORAGE}/pop/video.mp4`, poster: `${STORAGE}/pop/poster.jpg`, duration: '3:56' },
    audio: { src: `${STORAGE}/pop/audio.mp3`, duration: '3:56' },
    page: {
      slug: 'giovana-meus-quinze',
      title: 'Giovana, meus quinze',
      cover: `${STORAGE}/pop/cover.jpg`,
      lyrics: PORTFOLIO_LYRICS.pop,
    },
  },
];

export function toWhatsAppExample(example: GenreExample): WhatsAppAudioExample {
  return {
    id: `wa-${example.id}`,
    recipient: example.recipient,
    genre: example.genre,
    occasion: example.occasion,
    audio: example.audio,
  };
}

export function toOuvirExample(example: GenreExample): OuvirPageExample {
  return {
    id: `ouvir-${example.id}`,
    slug: example.page.slug,
    name: example.recipient,
    title: example.page.title,
    genre: example.genre,
    occasion: example.occasion,
    cover: example.page.cover,
    audio: example.audio,
    lyrics: example.page.lyrics,
  };
}
